import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { getRoleHomePath } from './config/navigation';
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AgentDashboard = lazy(() => import('./pages/AgentDashboard'));
const TeamLeaderDashboard = lazy(() => import('./pages/TeamLeaderDashboard'));
const ManagerDashboard = lazy(() => import('./pages/ManagerDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ClientDashboard = lazy(() => import('./pages/ClientDashboard'));
const AgentReports = lazy(() => import('./pages/AgentReports'));
const TeamLeaderReports = lazy(() => import('./pages/TeamLeaderReports'));
const ManagerReports = lazy(() => import('./pages/ManagerReports'));
const ManagerPendingSales = lazy(() => import('./pages/ManagerPendingSales'));
const AdminReports = lazy(() => import('./pages/AdminReports'));
const ClientReports = lazy(() => import('./pages/ClientReports'));
const TargetsPage = lazy(() => import('./pages/TargetsPage'));
const MailPage = lazy(() => import('./pages/MailPage'));
const AdminUsersPage = lazy(() => import('./pages/AdminUsersPage'));
const AdminDescriptorsPage = lazy(() => import('./pages/AdminDescriptorsPage'));
const AdminCentersPage = lazy(() => import('./pages/AdminCentersPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));

const WorkspaceFallback = () => (
  <div className="crm-state-page" role="status" aria-live="polite">
    <div className="crm-state-card">
      <div className="crm-loading-mark" aria-hidden="true" />
      <p>Loading your workspace…</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="crm-state-page" role="status" aria-live="polite">
        <div className="crm-state-card">
          <div className="crm-loading-mark" aria-hidden="true" />
          <p>Loading your workspace…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const DashboardRouter = () => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  switch (user.role) {
    case 'agent':
      return <AgentDashboard />;
    case 'team_leader':
      return <TeamLeaderDashboard />;
    case 'manager':
      return <ManagerDashboard />;
    case 'director':
      return <ManagerDashboard />;
    case 'admin':
      return <AdminDashboard />;
    case 'client':
      return <ClientDashboard />;
    default:
      return <Navigate to="/login" replace />;
  }
};

const NotFoundPage = () => {
  const { user } = useAuth();
  const destination = user ? getRoleHomePath(user.role) : '/login';

  return (
    <main className="crm-state-page" data-testid="not-found-page">
      <section className="crm-state-card" aria-labelledby="not-found-title">
        <p className="crm-eyebrow">Error 404</p>
        <h1 id="not-found-title">Page not found</h1>
        <p>The page may have moved, or the address may be incorrect.</p>
        <Link className="crm-primary-link" to={destination}>
          {user ? 'Return to dashboard' : 'Go to sign in'}
        </Link>
      </section>
    </main>
  );
};

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<WorkspaceFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent"
            element={
              <ProtectedRoute allowedRoles={['agent']}>
                <AgentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent/reports"
            element={
              <ProtectedRoute allowedRoles={['agent']}>
                <AgentReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team-leader"
            element={
              <ProtectedRoute allowedRoles={['team_leader']}>
                <TeamLeaderDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team-leader/reports"
            element={
              <ProtectedRoute allowedRoles={['team_leader']}>
                <TeamLeaderReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager"
            element={
              <ProtectedRoute allowedRoles={['manager', 'director']}>
                <ManagerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/reports"
            element={
              <ProtectedRoute allowedRoles={['manager', 'director']}>
                <ManagerReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/pending-sales"
            element={
              <ProtectedRoute allowedRoles={['manager', 'director']}>
                <ManagerPendingSales />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['admin', 'director']}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute allowedRoles={['admin', 'director']}>
                <AdminReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/reports"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <ClientReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/client/descriptors"
            element={
              <ProtectedRoute allowedRoles={['client']}>
                <AdminDescriptorsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/targets"
            element={
              <ProtectedRoute allowedRoles={['admin', 'director']}>
                <TargetsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={['admin', 'director']}>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/descriptors"
            element={
              <ProtectedRoute allowedRoles={['admin', 'director']}>
                <AdminDescriptorsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/centers"
            element={
              <ProtectedRoute allowedRoles={['admin', 'director']}>
                <AdminCentersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/director/submit-sale"
            element={
              <ProtectedRoute allowedRoles={['director']}>
                <Navigate to="/manager" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mail"
            element={
              <ProtectedRoute allowedRoles={['agent', 'team_leader', 'manager', 'director', 'admin']}>
                <MailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/director/documents"
            element={
              <ProtectedRoute allowedRoles={['director', 'admin']}>
                <DocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
